/**
 * Application Shell Layout
 *
 * Structure (like Signal):
 *   ┌────────────────────────────────┐
 *   │ Header (full width, sticky)    │
 *   ├──────────┬─────────────────────┤
 *   │ Sidebar  │ Content (Outlet)    │
 *   │          │                     │
 *   └──────────┴─────────────────────┘
 *
 * Used as a layout route — renders <Outlet> for nested routes.
 */

import { ReactNode, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { RouteProgressBar } from './RouteProgressBar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { EmbeddedBridge } from './EmbeddedBridge';
import { PageViewTracker } from './PageViewTracker';
import { ProcessingBanner } from '../ProcessingBanner';
import { isEmbedded } from '../../utils/embedded';
import { LoadingShimmer } from '../ui';

/** Pages are lazy chunks (App.tsx); the shell stays mounted while one loads. */
function PageContent({ children }: { children?: ReactNode }) {
  return (
    <Suspense fallback={<LoadingShimmer rows={4} label="Loading page" data-testid="page-loading" />}>
      {children || <Outlet />}
    </Suspense>
  );
}

export interface AppShellProps {
  children?: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  if (isEmbedded()) {
    // Inside the Compass App's WebView (utils/embedded.ts): the native shell
    // owns navigation and the header, so render the page alone with
    // phone-sized padding and keep the shell informed of route changes.
    return (
      <div
        className="flex flex-col h-screen"
        style={{ background: 'var(--bg-primary)' }}
        data-testid="app-shell-embedded"
      >
        <ImpersonationBanner />
        <RouteProgressBar />
        <EmbeddedBridge />
        <PageViewTracker />
        <main className="flex-1 overflow-y-auto">
          <div style={{ maxWidth: '100%', padding: '8px 12px 96px' }}>
            <PageContent>{children}</PageContent>
          </div>
        </main>
        <ProcessingBanner />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* CO-113: Impersonation banner — only renders when active */}
      <ImpersonationBanner />
      {/* Top banner — full width, sticky */}
      <Header />
      {/* Page-load signal for the whole shell — see hooks/usePageLoading.ts. */}
      <RouteProgressBar />
      {/* Reports each page opened — Admin Settings → Usage. */}
      <PageViewTracker />

      {/* Below banner — sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto">
          {/*
            Wide canvas — Compass has a lot of multi-column dashboards
            and log views that benefit from screen real estate. The
            old max-w-7xl (1280px) left half the viewport empty on a
            standard 24"+ monitor. Pages that want narrower content
            (forms, login) constrain themselves; the shell just
            provides padding now.
          */}
          <div style={{ maxWidth: '100%', padding: '24px 32px' }}>
            <PageContent>{children}</PageContent>
          </div>
        </main>
      </div>
      {/* Floating notification for background Claude Vision processing */}
      <ProcessingBanner />
    </div>
  );
}
